import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    BlockVariant,
    BlockVariantBaseProps,
    BlockVariantProps,
    BlockVariantSide,
    getVariantCss,
    variantDefaultProps,
} from "@store/Components/Common/Block/variant";
import { COMMON, TYPOGRAPHY } from "@store/theme/common/design";
import { siteWidthStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import React from "react";
import styled, { css } from "styled-components";

// -- INTERFACES

export interface BlockBorderProps {
    width: string;
    type: string;
    color: string;
}

export interface BlockBaseProps {
    fullWidth?: boolean;
    variant?: BlockVariant;
    variantProps?: BlockVariantProps;
    variantSide?: BlockVariantSide;
    margin?: Array<string | null>;
    preserveSpacing?: boolean;
    backgroundImage?: string;
    removeMargin?: boolean;
    removeSidesMargin?: boolean;
    keepSidesMarginForHighRes?: boolean;
}

export interface BlockProps extends BlockBaseProps, BlockVariantBaseProps {}

// -- UTILS

const parseProps = ({
    fullWidth,
    variant = variantDefaultProps,
    variantProps,
    variantSide,
    margin = COMMON.spacing.content,
    preserveSpacing,
    backgroundImage,
    removeMargin,
    removeSidesMargin,
    keepSidesMarginForHighRes,
    containerCss,
    spacerCss,
    wrapperCss,
    ...otherProps
}: Partial<BlockProps>) => {
    const blockProps: BlockProps = {
        fullWidth,
        variant,
        variantProps,
        variantSide,
        margin,
        preserveSpacing,
        backgroundImage,
        removeMargin,
        removeSidesMargin,
        keepSidesMarginForHighRes,
        containerCss,
        spacerCss,
        wrapperCss,
    };

    return { blockProps, otherProps };
};

// -- COMPONENTS

export const BlockWrapper = styled.div<BlockProps>`
    ${props => props.wrapperCss}
    ${getVariantCss("wrapperCss")}
`;

export const BlockSpacer = styled.div<BlockProps>`
    ${siteWidthStyles()}

    ${props =>
        props.removeMargin
            ? css`
                  margin-top: 0;
                  margin-bottom: 0;
              `
            : breakpointValuesMediaQueries(
                  props.theme,
                  [{ values: props.margin ?? [], properties: ["margin-top", "margin-bottom"] }],
                  mediaOptions.min,
              )}
    ${props =>
        props.removeSidesMargin &&
        breakpointValuesMediaQueries(
            props.theme,
            [
                {
                    values: [0, 0, 0, 0, props.keepSidesMarginForHighRes ? COMMON.spacing.content[4] : 0],
                    properties: ["padding-right", "padding-left"],
                },
            ],
            mediaOptions.min,
        )}

    ${props =>
        props.fullWidth
            ? css`
                  max-width: none;
              `
            : null}
    ${props => props.spacerCss}
    ${getVariantCss("spacerCss")}
`;

const BlockContainerBase = styled.div<BlockProps>``;
export const BlockContainer = styled(BlockContainerBase)`
    ${({ preserveSpacing, backgroundImage }) => {
        return css`
            ${backgroundImage
                ? css`
                      background-image: url(${backgroundImage});
                      background-repeat: no-repeat;
                      background-size: cover;
                      background-position: center;
                  `
                : null}

            ${BlockContainerBase} & {
                > ${BlockSpacer} {
                    ${!preserveSpacing
                        ? css`
                              margin-top: ${TYPOGRAPHY.spacing.base};
                              margin-bottom: ${TYPOGRAPHY.spacing.base};
                          `
                        : null}
                    padding-left: 0;
                    padding-right: 0;
                }

                &:first-child > ${BlockSpacer} {
                    margin-top: 0;
                }
                &:last-child > ${BlockSpacer} {
                    margin-bottom: 0;
                }
            }
        `;
    }}

    ${props => props.containerCss}
    ${getVariantCss("containerCss")}
`;

const Block: React.FC<BlockProps & React.HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
    if (!React.Children.count(children)) {
        return null;
    }

    const { blockProps, otherProps } = parseProps(props);

    return (
        <BlockContainer {...otherProps} {...blockProps}>
            <BlockSpacer {...blockProps}>
                <BlockWrapper {...blockProps}>{children}</BlockWrapper>
            </BlockSpacer>
        </BlockContainer>
    );
};

Block.displayName = "Block";

export default Block;
